import type { SectionKey } from "@reactive-resume/schema";
import { useCallback } from "react";
import { create } from "zustand";

export type DialogName =
  | "resume"
  | "lock"
  | "import"
  | "import-ocr"
  | "import-linkedin"
  | "two-factor"
  | "my-withdrawals"
  | "get-withdrawal"
  | "request-with"
  | "get-user"
  | "filter-client"
  | "filter-ambassador"
  | "filter-admin"
  | "create-user"
  | "create-credit"
  | "filter-resume"
  | "filter-reference"
  | "filter-bonus"
  | "create-rate"
  | "filter-rate"
  | "create-coupon"
  | "create-zone"
  | "create-provider"
  | "filter-coupon"
  | "filter-withdrawal"
  | "filter-download"
  | "filter-provider"
  | "get-payment"
  | "payment"
  | "forfait"
  | "ai-packs"
  | "filter-payment"
  | "create-payment-provider"
  | "edit-provider"
  | "edit-zone"
  | "filter-transaction"
  | "filter-amba-withdrawal"
  | "get-coupon"
  | "filter-amba-coupon"
  | "alert-credit-balance"
  | "edit-template"
  | "translate"
  | "scoring"
  | SectionKey
  | "confirm-download"
  | "role-form"
  | "permission-form"
  | "admin-roles"
  /** Fil de conversation du centre d'aide (console support admin). */
  | "help-detail";

export type DialogMode = "create" | "update" | "duplicate" | "delete" | "read";

export type DialogPayload<T = unknown> = {
  id: DialogName;
  item?: T;
};

type Dialog<T = unknown> = {
  name: DialogName;
  mode: DialogMode;
  payload?: DialogPayload<T>;
};

type DialogState = {
  dialog: Dialog | null;
};

type DialogActions = {
  setDialog: <T>(dialog: Dialog<T> | null) => void;
};

export const useDialogStore = create<DialogState & DialogActions>()((set) => ({
  dialog: null,

  setDialog: (dialog) => {
    set({
      dialog,
    });
  },
}));

export const useDialog = <T = unknown>(name: DialogName) => {
  const dialog = useDialogStore((state) => {
    const normalizedName = name.startsWith("custom.") ? "custom" : name;

    return state.dialog?.name === normalizedName ? state.dialog : null;
  });

  const open = useCallback(
    (mode: DialogMode, payload?: DialogPayload<T>) => {
      useDialogStore.setState({
        dialog: {
          name,
          mode,
          payload,
        },
      });
    },
    [name],
  );

  const close = useCallback(() => {
    useDialogStore.setState({ dialog: null });
  }, []);

  return {
    isOpen: Boolean(dialog),
    mode: dialog?.mode,
    payload: dialog?.payload as DialogPayload<T> | undefined,
    open,
    close,
  };
};
